/*
 * Copyright (c) 2019 AccelByte Inc. All Rights Reserved
 * This is licensed software from AccelByte Inc, for limitations
 * and restrictions contact your company contract manager.
 */

import classnames from "classnames";
import * as React from "react";
import { Subscribe } from "unstated";
import BasicLogic from "../../../../../Logic/Basic/basicLogic";
import MarkdownDocumentLogic from "../../../../../Logic/Legal/MarkdownDocumentLogic";
import { InvalidLocalizedPolicyData } from "../../../../../api/legal/models/legal";
import { LocalizedPolicy, LocalizedPolicyInformation, PolicyList } from "../../../../../api/legal/models/policies";
import { AppState } from "../../../../../app-state/AppState";
import { getCurrentLanguage, t } from "../../../../../i18n/i18n";
import { makeSafeReactInnerHTML } from "../../../../../utils/makeSafeReactInnerHTML";
import { ErrorContainer, FlexGrid, LoadingBarIcon, WithPropsGuard } from "../../../_common";
import DocumentNavigator from "../../component/DocumentNavigator/DocumentNavigator";
import { getLocalizedPolicyInfo, getLocalizedPolicyVersionPublishDateFromPolicies } from "../../utils";
import styles from "./DocumentContainer.module.scss";

interface BaseProps {
  policies: PolicyList | null;
  localizedPolicy: LocalizedPolicy | LocalizedPolicyInformation;
  isLoading: boolean;
  className?: string;
}

interface Props extends BaseProps {
  basicLogic: BasicLogic;
}

interface State {
  error: Error | null;
  hash: string | null;
}

const Loading = () => (
  <FlexGrid flex={2} className={styles.loadingContainer}>
    <LoadingBarIcon />
  </FlexGrid>
);

const Error = ({ error }: { error: Error | null }) => (
  <ErrorContainer description={t("DocumentContainer.fileNotFound")} />
);

const DocumentContainer = WithPropsGuard(
  class DocumentContainer extends React.Component<Props, State> {
    logic: MarkdownDocumentLogic;

    constructor(props: Props) {
      super(props);
      const { localizedPolicy } = props;
      this.state = {
        error: null,
        hash: window.location.hash,
      };
      try {
        this.logic = new MarkdownDocumentLogic({ localizedPolicy });
        this.logic.subscribe(() => this.setState({}));
      } catch (error) {
        this.state = {
          error,
          hash: window.location.hash,
        };
      }
    }

    componentDidMount(): void {
      if (this.state.error) return;
      this.logic.init();
    }

    private renderNavigator() {
      const { error } = this.state;
      if (error) return null;

      const { headings, headerPrefix } = this.logic.state;
      if (!headings || !headings.length) return null;
      // SA-61 Implement zendesk support : start
      return (
        <div className={styles.navigator}>
          <DocumentNavigator className={styles.documentNavigator} headerPrefix={headerPrefix} headings={headings} />
        </div>
      );
      // SA-61 Implement zendesk support : end
    }

    private renderContent() {
      const { htmlString } = this.logic.state;
      const { hash } = this.state;
      const { localizedPolicy, policies } = this.props;
      if (!htmlString) return null;

      const details = getLocalizedPolicyInfo({ localizedPolicy, policies });
      const breadcrumbs: string[] = [];
      // SB-260 replace breadcrum with spacename and document date: START
      let docDate = "";

      if(details.docDate) {
        const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
        const date = new Date(details.docDate);
        docDate = `${monthNames[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
      }
      // SB-260 replace breadcrum with spacename and document date: END

      if (details.namespace) breadcrumbs.push(details.namespace);

      const date = getLocalizedPolicyVersionPublishDateFromPolicies({ localizedPolicy, policies });
      if (!!date) {
        const currentLanguage = getCurrentLanguage();
        const formattedDate = Intl.DateTimeFormat(currentLanguage, {
          year: "numeric",
          month: "long",
          day: "numeric",
        }).format(new Date(date));
        breadcrumbs.push(formattedDate);
      }

      if (!!hash) {
        const copyHash = hash;
        this.setState({ hash: null });
        setTimeout(() => {
          window.location.href = copyHash;
          const el = document.getElementById(copyHash);
          if (el) el.scrollIntoView({ behavior: "smooth" });
        }, 10);
      }
      
      // SB-260 replace breadcrum with spacename and document date: START
      return (
        <FlexGrid flex={1} className={styles.content}>
          {details.namespace && (
            <div className={styles.breadcrumbs}>
              {details.namespace} &middot; {docDate}
            </div>
          )}
          <div className={styles.title}>{details.policyName}</div>
          <div className={styles.document} dangerouslySetInnerHTML={makeSafeReactInnerHTML(htmlString)} />
        </FlexGrid>
      );
      // SB-260 replace breadcrum with spacename and document date: END
    }

    render() {
      const { error } = this.state;
      // SB-260 styling error page: START
      if (error) return <div className={styles.documentError}><Error error={error} /></div>;
      // SB-260 styling error page: END

      const { className, localizedPolicy, isLoading } = this.props;
      const { isFetchingData, isGeneratingHTML, error: asLogicError } = this.logic.state;
      if (asLogicError) return <Error error={asLogicError} />;
      if (!localizedPolicy) return <Error error={new InvalidLocalizedPolicyData()} />;
      if (isFetchingData || isGeneratingHTML || isLoading) return <Loading />;
      return (
        <FlexGrid className={classnames(styles.documentContainer, className)}>
          {this.renderContent()}
          {/* // SA-61 Implement zendesk support : start */}
          {this.renderNavigator()}
          {/* // SA-61 Implement zendesk support : end */}
        </FlexGrid>
      );
    }
  },
  (prevProps, nextProps) =>
    prevProps.localizedPolicy.id !== nextProps.localizedPolicy.id ||
    (prevProps.policies || {}).toString() !== (nextProps.policies || {}).toString()
);

export default (props: BaseProps) => (
  <Subscribe to={[AppState]}>
    {(appState: AppState) => {
      const { basicLogic } = appState.state;
      return <DocumentContainer basicLogic={basicLogic} {...props} />;
    }}
  </Subscribe>
);
